// iOS-safe async clipboard helper.
//
// On iOS Safari, `navigator.clipboard.writeText(text)` only works when called
// from a direct user gesture. If the text has to be fetched asynchronously
// (e.g. an axios call) BEFORE the write, iOS rejects the call because the
// gesture context is lost across the `await`. Android Chrome and desktop
// browsers are more lenient and allow it.
//
// The fix is to pass a Promise to `ClipboardItem`: iOS will keep the
// user-gesture context alive until the promise resolves. This is the only
// reliable way to do async clipboard writes on iOS Safari.
//
// `copyTextAsync(fetcher)` takes a function that returns a Promise<string>.
// MUST be called directly inside a user event handler — do not `await`
// anything before calling it.

const buildBlob = (text) => new Blob([text], { type: "text/plain" });

export async function copyTextAsync(fetcher) {
  // Path 1: ClipboardItem-with-Promise (iOS Safari 13.4+, modern Chrome/Firefox)
  if (typeof window !== "undefined" && window.ClipboardItem && navigator.clipboard?.write) {
    try {
      const item = new window.ClipboardItem({
        "text/plain": Promise.resolve(fetcher()).then((text) => buildBlob(text || "")),
      });
      await navigator.clipboard.write([item]);
      return true;
    } catch (_) {
      // fall through to path 2
    }
  }
  // Path 2: legacy — resolve text first, then write. Works on Android/desktop
  // but NOT on iOS if a user gesture is required (we already tried the iOS
  // path above, so reaching here on iOS means ClipboardItem is unavailable —
  // we fall through to textarea + execCommand below).
  let text = "";
  try {
    text = await fetcher();
  } catch (e) {
    throw e;
  }
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text || "");
      return true;
    } catch (_) {
      // fall through to path 3
    }
  }
  // Path 3: textarea + execCommand (deprecated but still works on legacy iOS)
  try {
    const ta = document.createElement("textarea");
    ta.value = text || "";
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
}
